import { Card, CardContent } from "@/components/ui/card"
import type { Integration } from "@prescreen/lib/types"
import { apiFetch } from "@prescreen/lib/api-client"
import { cn } from "@prescreen/lib/utils"
import { useState, useEffect, useLayoutEffect, useRef, type RefObject } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSession } from "../contexts/SessionContext"
import IntegrationCard from "./IntegrationCard"
import IntegrationForm from "./IntegrationForm"
import { DeleteIntegrationDialog } from "./DeleteIntegrationDialog"

interface IntegrationsListTabProps {
  onIntegrationUpdated?: (integration: Integration) => void
}

type IntegrationsLocationState = {
  highlightIntegrationId?: string
}

function IntegrationListRow({
  integration,
  animateEntrance,
  rowRef,
  onEdit,
  onManageSecrets,
  onDelete,
  isDeleting,
}: {
  integration: Integration
  animateEntrance: boolean
  rowRef?: RefObject<HTMLDivElement | null>
  onEdit: (integration: Integration) => void
  onManageSecrets: (integration: Integration) => void
  onDelete?: (integration: Integration) => void
  isDeleting?: boolean
}) {
  return (
    <div
      ref={animateEntrance ? rowRef : undefined}
      className={cn(
        animateEntrance &&
          "origin-top motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-90 motion-safe:duration-500 motion-safe:ease-out",
      )}
    >
      <IntegrationCard
        integration={integration}
        onEdit={onEdit}
        onManageSecrets={onManageSecrets}
        onDelete={onDelete}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default function IntegrationsListTab({ onIntegrationUpdated }: IntegrationsListTabProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSession()
  const isAdmin = user?.role === "administrator"
  const entranceTargetRef = useRef<HTMLDivElement | null>(null)
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const fetchIntegrations = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await apiFetch("/api/integrations")

      if (!response.ok) {
        throw new Error("Failed to fetch integrations")
      }

      const result = await response.json()

      if (result.success && result.data?.integrations) {
        setIntegrations(result.data.integrations)
      } else {
        throw new Error(result.error || "Failed to load integrations")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchIntegrations()
  }, [])

  const highlightIntegrationId =
    (location.state as IntegrationsLocationState | null)?.highlightIntegrationId ?? undefined

  const entranceTargetId =
    highlightIntegrationId &&
    integrations.some((i) => i._id === highlightIntegrationId)
      ? highlightIntegrationId
      : undefined

  useLayoutEffect(() => {
    if (!entranceTargetId || !entranceTargetRef.current) {
      return
    }
    entranceTargetRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [entranceTargetId, integrations])

  useEffect(() => {
    if (!highlightIntegrationId || !entranceTargetId) {
      return
    }
    const id = window.setTimeout(() => {
      navigate(".", { replace: true, state: {} })
    }, 700)
    return () => window.clearTimeout(id)
  }, [highlightIntegrationId, entranceTargetId, navigate])

  const handleEditIntegration = (integration: Integration) => {
    setEditingIntegration(integration)
  }

  const handleIntegrationSaved = (updatedIntegration: Integration) => {
    setIntegrations((prev) =>
      prev.map((integration) =>
        integration._id === updatedIntegration._id ? updatedIntegration : integration,
      ),
    )

    setEditingIntegration(null)

    onIntegrationUpdated?.(updatedIntegration)
  }

  const handleCancelEdit = () => {
    setEditingIntegration(null)
  }

  const handleManageSecrets = (integration: Integration) => {
    if (!integration._id) {
      return
    }
    navigate(`/integrations/${integration._id}/secrets`)
  }

  const handleDeleteIntegration = (integration: Integration) => {
    setDeleteError(null)
    setDeleteTarget(integration)
  }

  const handleConfirmDelete = async (confirmName: string) => {
    if (!deleteTarget?._id) {
      return
    }

    try {
      setDeleting(true)
      setDeleteError(null)

      const response = await apiFetch(`/api/integrations/${deleteTarget._id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmName }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to delete integration")
      }

      setIntegrations((prev) => prev.filter((item) => item._id !== deleteTarget._id))
      setDeleteTarget(null)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete integration")
    } finally {
      setDeleting(false)
    }
  }

  const deleteSecretCount = deleteTarget?.oauthSecrets?.length ?? 0

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-gray-500">Loading integrations...</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-500">Error: {error}</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (integrations.length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-gray-500">
              No integrations found. Create your first integration in the &quot;Create Integration&quot;
              tab.
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (editingIntegration) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">Edit Integration</h2>
        </div>
        <IntegrationForm
          integration={editingIntegration}
          onIntegrationSaved={handleIntegrationSaved}
          onCancel={handleCancelEdit}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">
          Your Integrations ({integrations.length})
        </h2>
        <button
          type="button"
          onClick={() => void fetchIntegrations()}
          className="cursor-pointer text-sm text-blue-600 hover:text-blue-800"
        >
          Refresh
        </button>
      </div>

      {deleteError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {deleteError}
        </div>
      ) : null}

      <div className="grid gap-4">
        {integrations.map((integration) => {
          const animateEntrance = entranceTargetId === integration._id
          return (
            <IntegrationListRow
              key={integration._id}
              integration={integration}
              animateEntrance={animateEntrance}
              rowRef={animateEntrance ? entranceTargetRef : undefined}
              onEdit={handleEditIntegration}
              onManageSecrets={handleManageSecrets}
              onDelete={isAdmin ? handleDeleteIntegration : undefined}
              isDeleting={isAdmin && deleting && deleteTarget?._id === integration._id}
            />
          )
        })}
      </div>

      {isAdmin && deleteTarget ? (
        <DeleteIntegrationDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(open) => {
            if (!open && !deleting) {
              setDeleteTarget(null)
              setDeleteError(null)
            }
          }}
          integrationName={deleteTarget.name}
          secretCount={deleteSecretCount}
          onConfirm={handleConfirmDelete}
          isProcessing={deleting}
        />
      ) : null}
    </div>
  )
}
